import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const ANGULAR_LOGIN = 'https://accounts.honeysucklepub.com/auth/login';

const HomePage: React.FC = () => {

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            (entry.target as HTMLElement).style.opacity = '1';
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach((el) => {
      (el as HTMLElement).style.opacity = '0';
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ position: 'relative', zIndex: 1 }}>

      {/* HERO */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '120px 40px 80px',
        position: 'relative',
      }}>
        {/* Glow */}
        <div style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
          animation: 'glow-pulse 4s ease-in-out infinite',
        }} />

        <div className="container" style={{ maxWidth: '800px' }}>
          <div className="badge badge-white reveal" style={{ marginBottom: '24px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
            Now open — 200 Founding Member slots
          </div>

          <h1 className="reveal" style={{
            fontSize: 'clamp(48px, 8vw, 96px)',
            fontWeight: '700',
            letterSpacing: '-0.04em',
            lineHeight: '1',
            marginBottom: '24px',
            animationDelay: '0.1s',
          }}>
            Loyalty that<br />
            <span className="gradient-text">rewards you</span>
          </h1>

          <p className="reveal" style={{
            fontSize: '17px',
            color: 'var(--gray-400)',
            lineHeight: '1.6',
            maxWidth: '500px',
            margin: '0 auto 40px',
            fontWeight: '400',
            animationDelay: '0.2s',
          }}>
            Earn points every visit. Unlock exclusive benefits.
            Join Honeysuckle Rewards — free forever, or go premium with Honeysuckle+.
          </p>

          <div className="reveal" style={{ display: 'flex', gap: '12px', justifyContent: 'center', animationDelay: '0.3s' }}>
            <Link to="/register" className="btn-primary" style={{ fontSize: '14px', padding: '12px 24px' }}>
              Get Started Free →
            </Link>
            <a href={ANGULAR_LOGIN} className="btn-outline" style={{ fontSize: '14px', padding: '12px 24px', textDecoration: 'none' }}>
              Sign In
            </a>
          </div>

          {/* Stats */}
          <div className="reveal" style={{
            display: 'flex',
            gap: '48px',
            justifyContent: 'center',
            marginTop: '80px',
            animationDelay: '0.4s',
          }}>
            {[
              { value: '5', label: 'Branches' },
              { value: 'GHC 27', label: 'Wings Discount' },
              { value: '200', label: 'Founding Slots' },
              { value: 'Free', label: 'To Join' },
            ].map((stat) => (
              <div key={stat.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '700', letterSpacing: '-0.03em' }}>{stat.value}</div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px', letterSpacing: '0.02em' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="divider" />

      {/* HOW IT WORKS */}
      <section style={{ padding: '100px 0' }} id="rewards">
        <div className="container">
          <div className="reveal" style={{ marginBottom: '64px' }}>
            <span className="mono" style={{ marginBottom: '12px', display: 'block' }}>01 — HOW IT WORKS</span>
            <h2 style={{ fontSize: 'clamp(32px, 5vw, 56px)', letterSpacing: '-0.03em', lineHeight: '1.1' }}>
              Earn. Redeem. Repeat.
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
            {[
              { step: '01', title: 'Visit & Spend', desc: 'Earn 1 point for every GHC 5 spent across all Honeysuckle branches.' },
              { step: '02', title: 'Accumulate Points', desc: 'Track your points in real-time on your digital membership card.' },
              { step: '03', title: 'Redeem Rewards', desc: 'Trade points for free drinks, sides, wings and exclusive experiences.' },
            ].map((item) => (
              <div key={item.step} className="card reveal">
                <span className="mono" style={{ marginBottom: '16px', display: 'block' }}>{item.step}</span>
                <h3 style={{ fontSize: '20px', fontWeight: '600', letterSpacing: '-0.02em', marginBottom: '10px' }}>{item.title}</h3>
                <p style={{ fontSize: '14px', color: 'var(--gray-400)', lineHeight: '1.6' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* REWARDS TABLE */}
      <section style={{ padding: '100px 0' }}>
        <div className="container">
          <div className="reveal" style={{ marginBottom: '48px' }}>
            <span className="mono" style={{ marginBottom: '12px', display: 'block' }}>02 — REWARDS</span>
            <h2 style={{ fontSize: 'clamp(32px, 5vw, 56px)', letterSpacing: '-0.03em' }}>
              What your points unlock
            </h2>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
            {[
              { points: '50 pts', reward: 'Free soft drink, dip, or small add-on', tag: 'Popular' },
              { points: '100 pts', reward: 'Free side dish or local beer', tag: '' },
              { points: '120 pts', reward: 'Free draught beer of your choice', tag: '' },
              { points: '150 pts', reward: '10% discount voucher on total bill', tag: '' },
              { points: '200 pts', reward: 'Free classic wings portion', tag: 'Best Value' },
            ].map((item, i) => (
              <div key={i} className="reveal" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 28px',
                borderBottom: i < 4 ? '1px solid var(--border)' : 'none',
              }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--gray-300)', minWidth: '80px' }}>{item.points}</span>
                <span style={{ fontSize: '14px', color: 'var(--gray-300)', flex: 1, padding: '0 24px' }}>{item.reward}</span>
                {item.tag && (
                  <span className="badge badge-white" style={{ fontSize: '11px' }}>{item.tag}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* HONEYSUCKLE+ */}
      <section style={{ padding: '100px 0' }} id="plus">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <span className="mono reveal" style={{ marginBottom: '12px', display: 'block' }}>03 — PREMIUM</span>
              <h2 className="reveal" style={{ fontSize: 'clamp(32px, 5vw, 56px)', letterSpacing: '-0.03em', lineHeight: '1.1', marginBottom: '20px' }}>
                Honeysuckle+
              </h2>
              <p className="reveal" style={{ fontSize: '15px', color: 'var(--gray-400)', lineHeight: '1.7', marginBottom: '32px' }}>
                The premium tier for those who live at Honeysuckle.
                Get GHC 27 off wings every visit, earn points faster,
                and unlock founding member status — limited to 200 members only.
              </p>
              <Link to="/register" className="btn-primary reveal">
                Claim Your Spot →
              </Link>
            </div>

            {/* Pricing Card */}
            <div className="reveal">
              <div style={{
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '16px',
                padding: '40px',
                background: 'rgba(255,255,255,0.03)',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                }} />

                <div style={{ marginBottom: '32px' }}>
                  <span className="badge badge-white" style={{ marginBottom: '20px' }}>Founding Member</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '16px' }}>
                    <span style={{ fontSize: '48px', fontWeight: '700', letterSpacing: '-0.03em' }}>GHC 80</span>
                    <span style={{ color: 'var(--gray-500)', fontSize: '14px' }}>/month</span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '32px' }}>
                  {[
                    'GHC 27 off wings every visit',
                    '1.5× points multiplier',
                    'Access to exclusive rewards',
                    'Priority event access',
                    'Status recognition — all branches',
                    'Break even in just 3 visits',
                  ].map((benefit) => (
                    <div key={benefit} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#fff', fontSize: '14px' }}>✓</span>
                      <span style={{ fontSize: '14px', color: 'var(--gray-300)' }}>{benefit}</span>
                    </div>
                  ))}
                </div>

                <Link to="/register" className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '14px' }}>
                  Join Honeysuckle+
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* VALUE PROP TABLE */}
      <section style={{ padding: '100px 0' }}>
        <div className="container">
          <div className="reveal" style={{ marginBottom: '48px', textAlign: 'center' }}>
            <span className="mono" style={{ marginBottom: '12px', display: 'block' }}>04 — VALUE</span>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', letterSpacing: '-0.03em' }}>
              The math speaks for itself
            </h2>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', maxWidth: '700px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '16px 28px', borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.03)' }}>
              <span style={{ fontSize: '12px', color: 'var(--gray-500)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Visits / Month</span>
              <span style={{ fontSize: '12px', color: 'var(--gray-500)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Total Savings</span>
              <span style={{ fontSize: '12px', color: 'var(--gray-500)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Net Value</span>
            </div>
            {[
              { visits: '3', savings: 'GHC 81', net: 'Break-even' },
              { visits: '5', savings: 'GHC 135', net: '~1 wings portion free' },
              { visits: '8', savings: 'GHC 216', net: 'GHC 136 net gain' },
            ].map((row, i) => (
              <div key={i} className="reveal" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                padding: '18px 28px',
                borderBottom: i < 2 ? '1px solid var(--border)' : 'none',
              }}>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>{row.visits}</span>
                <span style={{ fontSize: '14px', color: 'var(--gray-300)' }}>{row.savings}</span>
                <span style={{ fontSize: '14px', color: 'var(--gray-400)' }}>{row.net}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* BRANCHES */}
      <section style={{ padding: '100px 0' }} id="branches">
        <div className="container">
          <div className="reveal" style={{ marginBottom: '48px' }}>
            <span className="mono" style={{ marginBottom: '12px', display: 'block' }}>05 — LOCATIONS</span>
            <h2 style={{ fontSize: 'clamp(32px, 5vw, 56px)', letterSpacing: '-0.03em' }}>
              Find us across Accra
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {[
              { name: 'Labone', address: 'High Street' },
              { name: 'Osu', address: 'Oxford Street' },
              { name: 'East Legon', address: 'American House' },
              { name: 'East Legon Extension', address: 'Community 1' },
              { name: 'Airport', address: 'Airport Residential' },
              { name: 'Spintex', address: 'Spintex' },
            ].map((branch) => (
              <div key={branch.name} className="card reveal" style={{ padding: '20px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff', marginBottom: '14px' }} />
                <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>{branch.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>{branch.address}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* CTA FOOTER */}
      <section style={{ padding: '120px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="reveal" style={{ fontSize: 'clamp(40px, 7vw, 80px)', letterSpacing: '-0.04em', lineHeight: '1', marginBottom: '24px' }}>
            Ready to join?
          </h2>
          <p className="reveal" style={{ color: 'var(--gray-400)', fontSize: '16px', marginBottom: '40px' }}>
            Free to join. Instant rewards. Cancel anytime.
          </p>
          <div className="reveal" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Link to="/register" className="btn-primary" style={{ fontSize: '14px', padding: '12px 28px' }}>
              Create Free Account →
            </Link>
            <a href={ANGULAR_LOGIN} className="btn-outline" style={{ fontSize: '14px', padding: '12px 28px', textDecoration: 'none' }}>
              Sign In
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '32px 0', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: 'var(--gray-600)' }}>© 2026 Honeysuckle. All rights reserved.</span>
          <span className="mono">Honeysuckle Rewards</span>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;